import { useLocation } from "react-router-dom";
import { useIsDesktopViewport } from "@app/shared/hooks/useIsDesktopViewport";
import { orderPaths } from "./orderPaths";

/**
 * Router `state` marker for call sites that link into a page below Order
 * Details directly from the Main page (e.g. `DropoffSection`'s "View Stop
 * Details" in single-dropoff tracking) rather than from Order Details
 * itself. Pass this as `navigate(path, { state: backPathFromHome })` at
 * such a call site, and `useOrderDetailsBackPath` will route the back
 * button to Main instead of guessing Order Details from the viewport.
 */
export const backPathFromHome = { backTo: "home" } as const;

type BackPathState = { backTo?: "home" } | null;

/**
 * Where a page nested below Order Details (Pickup/Dropoff, Events, any
 * `EntityDetailsPage`-based page, ...) should send its back button. Every
 * such call site should go through this instead of hardcoding
 * `orderPaths.details(...)`, so new pages automatically pick up the right
 * target.
 *
 * On mobile/tablet, Order Details is still that parent by default. On
 * desktop, it no longer is: `DesktopTrackingShell` shows the Main page
 * (`DesktopHomePage`, i.e. `OrderDetailsPage` with the logo header — see
 * that file) as the panel's root, so Order Details' own back-button screen
 * is a redundant hop the user never asked to visit — the shared parent
 * becomes the Main page's URL directly.
 *
 * That viewport-based guess is only a default, though — it assumes the
 * page was reached via Order Details. A handful of pages are also directly
 * reachable from Main itself (see `backPathFromHome` above); for those, the
 * navigating call site stamps `location.state`, which this hook prefers
 * over the guess so Back returns to the screen the user actually came from.
 */
export function useOrderDetailsBackPath(trackCode: string, isDropoffTracking: boolean): string {
  const isDesktop = useIsDesktopViewport();
  const { state } = useLocation() as { state: BackPathState };

  if (state?.backTo === "home") return orderPaths.home(trackCode, isDropoffTracking);

  return isDesktop ? orderPaths.home(trackCode, isDropoffTracking) : orderPaths.details(trackCode, isDropoffTracking);
}
